import { ApiService } from 'services/api.service';
import { NodeTypes } from 'dto/MetadataDto';
import { NodeConnectionUi } from './NodeConnectionUi';
import { NodeConnection, NodeConnectionNames } from 'dto/NodeConnection';
import { List } from 'linqts';
import { Output } from '@angular/core';
import { FlowUi } from './FlowUi';
import { BaseNodeConfig } from 'dto/NodeConfig';
import { Observable } from 'rxjs';

export class NodeUi {

    constructor(private api:ApiService, public dto:BaseNodeConfig, flow:FlowUi){
        this.Reset(dto);
        this.Flow = flow;
    }

    Connections:NodeConnectionUi[];
    /** Nodes graphically connected below this node.
     * 
     * During compile this property is changed, as the graph is being constructed.
     * The above predicate is only valid once the flow has been fully compiled.
     */
    Childs: NodeUi[];
    /** Nodes graphically connected above this node.
     * 
     * During compile this property is changed, as the graph is being constructed.
     * The above predicate is only valid once the flow has been fully compiled.
     * */
    Parents: NodeUi[];
    /** The flow that this node belongs to. */
    Flow:FlowUi;
    /** The template of this node (if any) */
    Template:any;
    /** The metadata of this node (if any) */
    MetaData:any;
    /** Width of the box needed to fit this node, and all child nodes below. */
    ColumnWidth: number;
    /** Y-coordinate of the top of this node */
    Y: number;
    /** X-coordinate of the left side of this node */
    X: number;
    /** Width of this node.
     * 
     * The default value is overridden by the Flow.Compile()
     */
    UiWidth: number;
    /** Height of this node.
     * 
     * The default value is overridden by the Flow.Compile()
     */
    UiHeight: number = 0;
    /** Highlight this node in the interface.
     * 
     * Example: When a user hovers the mouse over a connectionpoint that is affected by this node, the node is highlighted in the tree.
     */
    Highlight:boolean = false;
    /** The label to shown on this node */
    UiLabel:string = "";
    /** UNUSED: TODO: Determine what to do with this. See https://github.com/Traggid/FlowDesigner/commit/8b242fb#all_commit_comments */
    EndsGroup:boolean = false;

    /**
     * Reset all properties of this object that should be recalculated on a re-compile / -render.
     */
    public Reset(dto:BaseNodeConfig)
    {   
        this.dto = dto;
        // TODO: Make distinct set If there are two different connections to the same child node, it should not be drawn twice.
        this.Childs = [];
        // TODO: Make distinct set. If there are two different connections from the same parent node, it should not be included twice.
        this.Parents = [];
        this.Connections = [];
        this.Template = null;
        this.MetaData = null;
        this.ColumnWidth = 0;
        this.X = 0; 
        this.Y = 0;
        this.UiWidth = 0;
        this.UiHeight = 0;
        this.Highlight = false;
        this.EndsGroup = false;
    }

    ReRender() {
        this.EndsGroup = this.IsEndOfGroup();
        this.Connections.forEach(conn => conn.UpdateDrawOffsets());
        this.getUiLabel().subscribe(result => this.UiLabel = result);
    }

    /** Returns the well-defined single leaf node that ends this branch
     * Returns null if not well-defined (e.g. branch has multiple sub-branches)
     * Returns this object itself if it has no children.
     */
    public GetSingleEndOfBranch():NodeUi {
        if (this.IsEndOfGroup())
        {
            if (this.Childs.length == 0)
                return this;
            if (this.Childs.length > 1)
                return null;
            return this.Childs[0].GetSingleEndOfBranch();
        }
        return this.GetEndOfThisGroup().GetSingleEndOfBranch();
    }

    /** Returns the well-defined single node that ends this group
     * This is the first node down the tree that has either of:
     * - zero children
     * - a child that does not always trigger
     */
    public GetEndOfThisGroup():NodeUi {
        if (this.IsEndOfGroup())
            return this;
        return this.Childs[0].GetEndOfThisGroup();
    }


    /** Returns true if the node is the node at the end of its group.
     * That is, the node has either of:
     * - zero children
     * - no child that always triggers
     */
    public IsEndOfGroup():boolean {
        return this.Childs.length == 0 // No children? It's the end of a group
            || this.Childs.length > 1 // Multiple children? It's the end of a group.
            || this.Childs.length == 1 // A single child?
            && !this.Childs[0].dto.TriggeredBy.some(trigger => // And there is no child that has a trigger
                trigger.Identifier == this.dto.Identifier // That comes from this node
                && trigger.Name == NodeConnectionNames.ALWAYS_TRIGGERED); // and triggers "Always"
    }

    /** Returns true only if the branch below this tree does not have sub-branches and every
     * NodeConnection (if any) from parent to child triggers on NodeConnectionNames.ALWAYS_TRIGGERED
     */
    public IsRootOfGroupOnly():boolean {
        return !this.GetEndOfThisGroup().Childs;
    }

    /** Returns all ancestor nodes of this node.
     * This function recurses up the tree towards the root node.
     * @param namedOnly Only return nodes whose Name property is set.
     */
    public GetAncestors(namedOnly?:boolean):NodeUi[] {
        var ancestors = [];
        for (let parentNode of this.Parents)
        {
            if (parentNode.dto.Name != null || !namedOnly)
                ancestors.push(parentNode);
            ancestors.push(...parentNode.GetAncestors(namedOnly))
        }
        return ancestors;
    }

    /** Recursively update the Y-axis position of all children of this node.
     * 
     */
    public UpdateY() {
        let additionalHeight = 0;
        if (this.Childs.length > 1 || this.Connections.find(conn => conn.Dto.Name != NodeConnectionNames.ALWAYS_TRIGGERED) || this.Childs.find(child => child.Parents.length > 1))
            additionalHeight = 50;
        for (var child of this.Childs) {
            child.Y = Math.max(this.Y + this.Flow.NodeHeight + additionalHeight, child.Y);
            child.UpdateY();
        }
    }

    /** Recursively update the X-axis position and columnwidth of this node, and all children.
     * 
     * @param parentXPos The X-axis position of the parent node.
     */
    public UpdateColumnWidth(parentXPos: number) {
        this.X = parentXPos;
        if (this.Childs.length == 0)
            this.ColumnWidth = this.UiWidth;
        else {
            var columnWidth = 0;
            for (var child of this.Childs) {
                if (!child.ColumnWidth)
                {
                    child.UpdateColumnWidth(this.X + columnWidth);
                    columnWidth += child.ColumnWidth;
                }
            }
            // We need at least enough space for the node itself.
            this.ColumnWidth = Math.max(columnWidth, this.UiWidth);
        }
    }

    /** Shift non-always children recursively down the tree whenever the node has an "Always" output.
     * This function is used to transform the tree from a plain tree as constructed from the DTOs
     * to one that graphically matches our execution logic more closely.
     */
    public ShiftChildren()
    {
        // Find the child (if any) that Always triggers.
        let alwaysChild = this.Childs.find(child =>
            child.dto.TriggeredBy.some(trigger =>
                trigger.Identifier == this.dto.Identifier
                && trigger.Name == NodeConnectionNames.ALWAYS_TRIGGERED));
        // If such a child exitst:
        if (alwaysChild)
        {
            // Get all the other children of this node.
            let otherChilds = this.Childs.filter(child => child.dto.Identifier != alwaysChild.dto.Identifier);
            // For each other child:  Shift down along the "Always" line.
            otherChilds.forEach(child => {
                // Remove this node as a parentnode
                child.Parents.splice(child.Parents.findIndex(parent => parent.dto.Identifier != this.dto.Identifier),1)
                // Add the alwaysChild of this node as the new parent.
                child.Parents.push(alwaysChild);
                // Add this node as a child of the alwaysChild
                alwaysChild.Childs.push(child);
            });
            // Leave only the alwaysChild as child of the current node.
            this.Childs = [alwaysChild];
        }
        // Recursively call this function down the tree.
        this.Childs.forEach(child => child.ShiftChildren());
    }

    public toString():string {
        return this.dto.Name || this.dto.Identifier;
    }

    public getUiLabel():Observable<string> {
        return Observable.create(obs => {
            switch (this.Template.NodeType)
            {
            case NodeTypes.FlowOutput:
                obs.next(this.dto.TemplateValues.OutputValue);
                obs.complete();
                break;
            case NodeTypes.SubflowNode:
                this.api.getFlowNames().subscribe(result => {
                    obs.next(result.find(x => x.Identifier == this.dto.TemplateValues.FlowId).Name);
                    obs.complete();
                })
                break;
            case NodeTypes.GoToNode:
                let node = this.Flow.Nodes.find(node => node.dto.Identifier == this.dto.TemplateValues.NodeId)
                if (node)
                    obs.next(node.toString());
                obs.next(null);
                obs.complete();
                break;
            case NodeTypes.ExpressionNode:
                obs.next(this.dto.TemplateValues.NodeExpression);
                obs.complete();
                break;
            default:
                obs.next(this.dto.TemplateValues.Label);
                obs.complete();
            }
        });
    }
}



// WEBPACK FOOTER //
// C:/git/flowdesigner/src/classes/NodeUi.ts